import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WifiOff, RefreshCw, ShieldAlert, LogIn, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  MAX_AUTO_ATTEMPTS,
  getChatConn,
  requestChatReconnect,
  subscribeChatConn,
  type ChatConnState,
} from '@/lib/chatConnection';
import {
  startChatDisconnectSignal,
  stopChatDisconnectSignal,
} from '@/lib/chatAlertSignal';

function tempoFora(since: number | null): string {
  if (!since) return '';
  const min = Math.floor((Date.now() - since) / 60_000);
  if (min < 1) return 'agora há pouco';
  if (min === 1) return 'há 1 minuto';
  if (min < 60) return `há ${min} minutos`;
  const h = Math.floor(min / 60);
  return h === 1 ? 'há 1 hora' : `há ${h} horas`;
}

/**
 * Alerta global e persistente da queda do ChatContar.
 *
 * Fica na tela até a conexão voltar ou o usuário entrar de novo. "Ciente"
 * apenas encolhe o aviso para uma pastilha — nunca o esconde por completo,
 * porque enquanto o chat está fora ninguém recebe mensagem.
 */
export default function ChatDisconnectedAlert() {
  const [conn, setConn] = useState<ChatConnState>(getChatConn());
  const [encolhido, setEncolhido] = useState(false);
  const [, forcarRelogio] = useState(0);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  useEffect(() => subscribeChatConn(setConn), []);

  const { status, attempts, since, message } = conn;
  const caido = status === 'disconnected' || status === 'expired';
  // Em "reconnecting" sem queda registrada o app só está subindo — não é aviso.
  const visivel = caido || (status === 'reconnecting' && since !== null);

  // Sinaliza fora da página: título da aba piscando e notificação nativa
  // (no ChatContar desktop vira toast + botão piscando na barra de tarefas).
  useEffect(() => {
    if (caido) startChatDisconnectSignal(status);
    else stopChatDisconnectSignal();
  }, [caido, status]);

  useEffect(() => () => stopChatDisconnectSignal(), []);

  // Volta a expandir sempre que o estado piora.
  useEffect(() => {
    if (status === 'expired') setEncolhido(false);
    if (status === 'connected') setEncolhido(false);
  }, [status]);

  // Mantém o "desconectado há X" andando.
  useEffect(() => {
    if (!visivel) return;
    const id = setInterval(() => forcarRelogio((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, [visivel]);

  if (!visivel) return null;

  const reconectando = status === 'reconnecting';
  const expirada = status === 'expired';
  const esgotou = attempts >= MAX_AUTO_ATTEMPTS;
  const pedeLogin = expirada || esgotou;

  const entrarNovamente = async () => {
    try { await signOut(); } catch { /* ignore */ }
    navigate('/login');
  };

  if (encolhido) {
    return (
      <button
        type="button"
        onClick={() => setEncolhido(false)}
        className="fixed bottom-4 right-4 z-[100] flex items-center gap-2 rounded-full border border-destructive-borda bg-destructive-suave px-4 py-2 text-sm font-medium text-destructive-texto shadow-lg backdrop-blur print:hidden"
      >
        <WifiOff className="h-4 w-4 shrink-0" />
        ChatContar desconectado
        <ChevronUp className="h-4 w-4 shrink-0" />
      </button>
    );
  }

  const titulo = expirada
    ? 'Sua sessão do ChatContar expirou'
    : reconectando
      ? 'Reconectando ao ChatContar…'
      : 'ChatContar desconectado';

  const detalhe =
    message ??
    (reconectando
      ? 'Aguarde: a tentativa é encerrada com um resultado em até 15 segundos.'
      : 'Você não está recebendo novas mensagens.');

  return (
    <div className="fixed inset-x-0 top-0 z-[100] flex justify-center px-4 pt-3 print:hidden">
      <div
        role="alert"
        aria-live="assertive"
        className={`w-full max-w-2xl rounded-xl border-2 px-4 py-3 shadow-xl backdrop-blur ${
          reconectando
            ? 'border-warning/50 bg-warning/15'
            : 'border-destructive bg-destructive/15 animate-pulse-soft'
        }`}
      >
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-start gap-3 min-w-0">
            {reconectando ? (
              <RefreshCw className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-warning-texto" />
            ) : expirada ? (
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            ) : (
              <WifiOff className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground">{titulo}</p>
              <p className="text-xs text-muted-foreground">{detalhe}</p>
              {since !== null && !reconectando && (
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Fora do ar {tempoFora(since)}
                  {attempts > 0 && ` · ${attempts} tentativa${attempts > 1 ? 's' : ''}`}
                </p>
              )}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant={pedeLogin ? 'outline' : 'default'}
              disabled={reconectando}
              onClick={() => { void requestChatReconnect(); }}
            >
              <RefreshCw className={`mr-1 h-4 w-4 ${reconectando ? 'animate-spin' : ''}`} />
              {reconectando ? 'Reconectando…' : 'Reconectar'}
            </Button>

            {pedeLogin && (
              <Button size="sm" variant="destructive" onClick={() => { void entrarNovamente(); }}>
                <LogIn className="mr-1 h-4 w-4" />
                Entrar novamente
              </Button>
            )}

            {!expirada && !reconectando && (
              <Button size="sm" variant="ghost" onClick={() => setEncolhido(true)}>
                Ciente
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
